export function findHTMLFragment(targetHTML, source) {

    // ── 1. Normalise inputs into DOM elements ──────────────────────────────────
  
    const parser = new DOMParser();
  
    /** Parse an HTML string into a temporary wrapper element */
    function parseHTML(html) {
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      return doc.body.firstChild; // the wrapper <div>
    }
  
    const sourceEl  = typeof source    === 'string' ? parseHTML(source)    : source;
    const targetEl  = typeof targetHTML === 'string' ? parseHTML(targetHTML) : targetHTML;
  
    // ── 2. Helpers ─────────────────────────────────────────────────────────────
  
    /** Collapse all whitespace sequences to a single space and trim */
    const normalise = (str) => str.replace(/\s+/g, ' ').trim();
  
    /** Extract text content, normalised */
    const text = (el) => normalise(el.textContent || '');
  
    /** Serialise an element's inner HTML, normalised */
    const innerNorm = (el) => normalise(el.innerHTML || '');
  
    // ── 3. Strategy A: exact innerHTML match ──────────────────────────────────
    // Walk every element in the source and compare its normalised innerHTML
    // against the normalised target innerHTML.
  
    function exactInnerHTMLMatch() {
      const targetNorm = innerNorm(targetEl);
      const walker = document.createTreeWalker(
        sourceEl,
        NodeFilter.SHOW_ELEMENT,
        null
      );
  
      let node;
      while ((node = walker.nextNode())) {
        if (normalise(node.innerHTML) === targetNorm) {
          return {
            found:    true,
            strategy: 'exactInnerHTML',
            element:  node,
            tagName:  node.tagName.toLowerCase(),
            id:       node.id || null,
            text:     text(node),
            html:     node.outerHTML,
          };
        }
      }
      return null;
    }
  
    // ── 4. Strategy B: text content match inside element ──────────────────────
    // Find an element whose normalised text content exactly equals the
    // normalised text content of the target.
  
    function textContentMatch() {
      const targetText = text(targetEl);
      const walker = document.createTreeWalker(
        sourceEl,
        NodeFilter.SHOW_ELEMENT,
        null
      );
  
      let node;
      while ((node = walker.nextNode())) {
        if (text(node) === targetText) {
          return {
            found:    true,
            strategy: 'textContent',
            element:  node,
            tagName:  node.tagName.toLowerCase(),
            id:       node.id || null,
            text:     text(node),
            html:     node.outerHTML,
          };
        }
      }
      return null;
    }
  
    // ── 5. Strategy C: target text is CONTAINED within an element ─────────────
    // The target may be a substring of a larger element's content.
    // Find the deepest element that fully contains the target text.
  
    function containedTextMatch() {
      const targetText = text(targetEl);
      let bestMatch = null;
      let bestLength = Infinity;
  
      const walker = document.createTreeWalker(
        sourceEl,
        NodeFilter.SHOW_ELEMENT,
        null
      );
  
      let node;
      while ((node = walker.nextNode())) {
        const nodeText = text(node);
        if (nodeText.includes(targetText)) {
          // Prefer the most specific (shortest text = deepest) match
          if (nodeText.length < bestLength) {
            bestLength = nodeText.length;
            bestMatch  = node;
          }
        }
      }
  
      if (bestMatch) {
        const nodeText   = text(bestMatch);
        const startIndex = nodeText.indexOf(targetText);
        return {
          found:      true,
          strategy:   'containedText',
          element:    bestMatch,
          tagName:    bestMatch.tagName.toLowerCase(),
          id:         bestMatch.id || null,
          text:       text(bestMatch),
          html:       bestMatch.outerHTML,
          // Where inside the element's text the target starts/ends
          startIndex,
          endIndex:   startIndex + targetText.length,
        };
      }
      return null;
    }
  
    // ── 6. Strategy D: target spans MULTIPLE sibling elements ─────────────────
    // The target HTML may begin in one sibling and end in another.
    // Collect siblings whose combined text contains the target text.
    //
    // Example:
    //   Source: <p>first part</p><p>second part</p>
    //   Target: <p>part</p><p>second</p>   ← straddles a boundary
    //
    // We slide a window over the children of each container element.
  
    function spanningMatch() {
      const targetText    = text(targetEl);
      const targetWords   = targetText.split(' ');
  
      // Check every element that could be a container of siblings
      const containers = [sourceEl, ...sourceEl.querySelectorAll('*')];
  
      for (const container of containers) {
        const children = Array.from(container.children);
        if (children.length < 2) continue;
  
        // Sliding window over children
        for (let start = 0; start < children.length; start++) {
          for (let end = start + 1; end <= children.length; end++) {
            const slice       = children.slice(start, end);
            const combinedText = normalise(slice.map(c => c.textContent).join(' '));
  
            if (combinedText.includes(targetText)) {
              return {
                found:     true,
                strategy:  'spanningElements',
                elements:  slice,
                tagNames:  slice.map(c => c.tagName.toLowerCase()),
                container: container,
                text:      combinedText,
                html:      slice.map(c => c.outerHTML).join(''),
                startNode: slice[0],
                endNode:   slice[slice.length - 1],
              };
            }
          }
        }
      }
      return null;
    }
  
    // ── 7. Strategy E: fuzzy word-boundary match ──────────────────────────────
    // If the target text starts or ends mid-sentence (partial node content),
    // find nodes whose text content STARTS WITH or ENDS WITH the target's
    // first / last sentence fragment.
  
    function fuzzyBoundaryMatch() {
      const targetText  = text(targetEl);
      // First ~50 chars and last ~50 chars of target as anchors
      const headAnchor  = targetText.slice(0, 50);
      const tailAnchor  = targetText.slice(-50);
  
      let headNode = null;
      let tailNode = null;
  
      const walker = document.createTreeWalker(
        sourceEl,
        NodeFilter.SHOW_ELEMENT,
        null
      );
  
      let node;
      while ((node = walker.nextNode())) {
        const nodeText = text(node);
        if (!headNode && nodeText.includes(headAnchor)) headNode = node;
        if (nodeText.includes(tailAnchor))               tailNode = node;
      }
  
      if (headNode && tailNode) {
        const isSameNode = headNode === tailNode;
        return {
          found:     true,
          strategy:  'fuzzyBoundary',
          headNode,
          tailNode,
          isSameNode,
          text:      targetText,
          elements:  isSameNode ? [headNode] : [headNode, tailNode],
        };
      }
      return null;
    }
  
    // ── 8. Run strategies in order of precision ────────────────────────────────
  
    const result =
      exactInnerHTMLMatch()  ||
      textContentMatch()     ||
      containedTextMatch()   ||
      spanningMatch()        ||
      fuzzyBoundaryMatch()   ||
      { found: false, strategy: 'none', text: null, element: null };
  
    return result;
  }
  